import React, { useState, useEffect } from 'react';
import SendNotificationModal from './SendNotificationModal.tsx';
import NotificationDetailView from './NotificationDetailView.tsx';
import ManageNotificationFamiliesModal from './ManageNotificationFamiliesModal.tsx';
import { linkify } from '../../utils/linkify.tsx';
import { AppIcon, notificationTypeIcon } from '../../icons/appIcons.tsx';
import EmptyState from '../common/EmptyState.tsx';

interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'general' | 'payment' | 'event' | 'urgent' | 'maintenance' | 'grounds' | 'horse';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  targetAudience: 'all' | 'active_families' | 'overdue_payments' | 'specific_families';
  specificFamilyIds: string | null;
  createdAt: string;
  updatedAt: string;
  expiresAt: string | null;
  isActive: boolean;
  creator: {
    firstName: string;
    lastName: string;
  };
  reads: Array<{
    id: string;
    familyId: string;
    readAt: string;
    family: {
      id: string;
      familyName: string;
      primaryContactFirstName: string;
      primaryContactLastName: string;
    };
  }>;
  targetFamilies?: Array<{
    id: string;
    familyName: string;
    primaryContactFirstName: string;
    primaryContactLastName: string;
  }>;
}

interface EditNotificationModalProps {
  notification: Notification;
  onClose: () => void;
  onSuccess: () => void;
}

function EditNotificationModal({ notification, onClose, onSuccess }: EditNotificationModalProps) {
  const [formData, setFormData] = useState({
    title: notification.title,
    message: notification.message,
    type: notification.type,
    priority: notification.priority,
    expiresAt: notification.expiresAt ? new Date(notification.expiresAt).toISOString().slice(0, 16) : ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors([]);

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/notifications/${notification.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          expiresAt: formData.expiresAt || null
        })
      });

      const data = await response.json();

      if (data.success) {
        onSuccess();
      } else {
        setErrors(data.errors?.map((err: any) => err.msg) || [data.message]);
      }
    } catch (error) {
      console.error('Error updating notification:', error);
      setErrors(['Failed to update notification. Please try again.']);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Edit Notification</h3>
              <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700">
                <AppIcon name="close" className="w-5 h-5" />
              </button>
            </div>

            {errors.length > 0 && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <ul className="list-disc list-inside text-red-700 text-sm">
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  required
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as any }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                  >
                    <option value="general">General</option>
                    <option value="payment">Payment</option>
                    <option value="event">Event</option>
                    <option value="urgent">Urgent</option>
                    <option value="maintenance">Maintenance</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as any }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Expires At (Optional)</label>
                <input
                  type="datetime-local"
                  value={formData.expiresAt}
                  onChange={(e) => setFormData(prev => ({ ...prev, expiresAt: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="px-6 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !formData.title || !formData.message}
                className="px-6 py-2 bg-ggaa-green text-white rounded-lg hover:bg-ggaa-green/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Updating...' : 'Update Notification'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function NotificationManagement() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingNotification, setEditingNotification] = useState<Notification | null>(null);
  const [viewingNotification, setViewingNotification] = useState<Notification | null>(null);
  const [managingFamiliesNotification, setManagingFamiliesNotification] = useState<Notification | null>(null);
  const [filter, setFilter] = useState<string>('all');

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/notifications', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        // Filter out expired notifications
        const activeNotifications = data.data.filter((notification: Notification) => {
          if (!notification.expiresAt) return true;
          return new Date(notification.expiresAt) > new Date();
        });
        setNotifications(activeNotifications);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (notification: Notification) => {
    if (!window.confirm(`Are you sure you want to delete "${notification.title}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/notifications/${notification.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        fetchNotifications();
      } else {
        alert('Failed to delete notification: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
      alert('Failed to delete notification. Please try again.');
    }
  };

  const getTypeColor = (type: string, priority: string) => {
    if (priority === 'urgent') return 'border-l-red-500 bg-red-50';
    
    switch (type) {
      case 'grounds': return 'border-l-green-600 bg-green-50';
      case 'horse': return 'border-l-amber-600 bg-amber-50';
      case 'urgent': return 'border-l-red-500 bg-red-50';
      case 'payment': return 'border-l-yellow-500 bg-yellow-50';
      case 'event': return 'border-l-blue-500 bg-blue-50';
      case 'maintenance': return 'border-l-gray-500 bg-gray-50';
      default: return 'border-l-blue-500 bg-blue-50';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTargetAudienceText = (targetAudience: string, specificFamilyIds: string | null) => {
    switch (targetAudience) {
      case 'all': return 'All Families';
      case 'active_families': return 'Active Families';
      case 'overdue_payments': return 'Families with Overdue Payments';
      case 'specific_families': {
        const familyCount = specificFamilyIds ? JSON.parse(specificFamilyIds).length : 0;
        return `${familyCount} Specific Families`;
      }
      default: return targetAudience;
    }
  };

  const filteredNotifications = notifications.filter(notification => {
    if (filter === 'all') return true;
    return notification.type === filter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ggaa-green"></div>
        <span className="ml-2 text-gray-600">Loading notifications...</span>
      </div>
    );
  }

  return (
    <div className="px-12 py-10">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Notification Management</h1>
          <p className="text-gray-600 mt-2">Create and manage notifications for GGAA families</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-ggaa-green text-white px-6 py-3 rounded-lg hover:bg-ggaa-green/90 transition-colors font-medium"
        >
          Create New Notification
        </button>
      </div>

      {/* Filters */}
      <div className="mb-6 flex space-x-2">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filter === 'all' 
              ? 'bg-ggaa-green text-white' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All ({notifications.length})
        </button>
        {['general', 'grounds', 'horse', 'payment', 'event', 'urgent', 'maintenance'].map(type => {
          const count = notifications.filter(n => n.type === type).length;
          return (
            <button
              key={type}
              onClick={() => setFilter(type)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === type 
                  ? 'bg-ggaa-green text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <span className="inline-flex items-center gap-1.5">
                <AppIcon name={notificationTypeIcon(type)} className="w-4 h-4" />
                {type.charAt(0).toUpperCase() + type.slice(1)} ({count})
              </span>
            </button>
          );
        })}
      </div>

      {/* Notifications List */}
      {filteredNotifications.length === 0 ? (
        <div className="bg-white rounded-xl shadow-lg py-6">
          <EmptyState
            icon="emptyInbox"
            title="No notifications found"
            description={
              filter === 'all'
                ? "You haven't created any notifications yet."
                : `No ${filter} notifications found.`
            }
          />
          <div className="text-center pb-6">
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-ggaa-green text-white px-6 py-2 rounded-lg hover:bg-ggaa-green/90 transition-colors"
            >
              Create Your First Notification
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredNotifications.map(notification => (
            <div
              key={notification.id}
              className={`bg-white rounded-xl shadow-lg border-l-4 ${getTypeColor(notification.type, notification.priority)} p-6`}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-3">
                    <AppIcon name={notificationTypeIcon(notification.type)} className="w-6 h-6 shrink-0" />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{notification.title}</h3>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span className="capitalize">{notification.type} • {notification.priority} priority</span>
                        <span>•</span>
                        <span>To: {getTargetAudienceText(notification.targetAudience, notification.specificFamilyIds)}</span>
                        <span>•</span>
                        <span>{notification.reads.length} families read</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-700 mb-4 line-clamp-4">{linkify(notification.message)}</p>
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center space-x-4">
                      <span>Created: {formatDate(notification.createdAt)}</span>
                      {notification.expiresAt && (
                        <span>Expires: {formatDate(notification.expiresAt)}</span>
                      )}
                    </div>
                    <span>By: {notification.creator.firstName} {notification.creator.lastName}</span>
                  </div>
                </div>
                <div className="ml-6 flex flex-col space-y-2">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setViewingNotification(notification)}
                      className="px-4 py-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors text-sm font-medium"
                    >
                      View
                    </button>
                    <button
                      onClick={() => setManagingFamiliesNotification(notification)}
                      className="px-4 py-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors text-sm font-medium"
                    >
                      Recipients
                    </button>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setEditingNotification(notification)}
                      className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors text-sm font-medium"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(notification)}
                    className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm font-medium"
                  >
                    Delete
                  </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Notification Modal */}
      {showCreateModal && (
        <SendNotificationModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchNotifications();
          }}
        />
      )}

      {/* Edit Notification Modal */}
      {editingNotification && (
        <EditNotificationModal
          notification={editingNotification}
          onClose={() => setEditingNotification(null)}
          onSuccess={() => {
            setEditingNotification(null);
            fetchNotifications();
          }}
        />
      )}

      {/* Manage Families Modal */}
      {managingFamiliesNotification && (
        <ManageNotificationFamiliesModal
          notification={managingFamiliesNotification}
          onClose={() => setManagingFamiliesNotification(null)}
          onSuccess={() => {
            setManagingFamiliesNotification(null);
            fetchNotifications();
          }}
        />
      )}

      {/* View Notification Details Modal */}
      {viewingNotification && (
        <NotificationDetailView
          notification={viewingNotification}
          onClose={() => setViewingNotification(null)}
        />
      )}
    </div>
  );
} 